import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button, type ButtonProps } from '@/components/ui/button';
import type { EstimateCommand } from '../../hooks/useEstimates';
import type { EstimateAction } from '../../lib/actions';
import type { Estimate } from '../../types';
import { RejectDialog } from './RejectDialog';

interface Props {
  estimate: Estimate;
  actions: Set<EstimateAction>;
  /** Unsaved edits: the server re-prices on every transition, so act on saved inputs only (plan ruling 4). */
  dirty: boolean;
  pending: boolean;
  onCommand: (command: EstimateCommand) => Promise<boolean>;
  onDelete: () => void;
  onConvert: () => void;
}

/** The lifecycle buttons the server would accept for this user (lib/actions.ts mirrors estimate-lifecycle.js). */
export function EstimateActionsBar({ estimate, actions, dirty, pending, onCommand, onDelete, onConvert }: Props) {
  const { t } = useTranslation('crm');
  const [rejectOpen, setRejectOpen] = useState(false);
  const [confirm, setConfirm] = useState<'delete' | 'mark-lost' | null>(null);
  const disabled = dirty || pending;
  const lifecycle = [...actions].filter((a) => a !== 'save' && a !== 'reprice' && a !== 'generate-deck');
  const blocked = estimate.status === 'draft' && (estimate.result?.warnings ?? []).some((w) => w.blocking);

  const button = (action: EstimateAction, label: string, onClick: () => void, variant: ButtonProps['variant'] = 'outline') =>
    actions.has(action) ? <Button size="sm" variant={variant} disabled={disabled} onClick={onClick}>{label}</Button> : null;

  if (!lifecycle.length && !blocked) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {button('request-approval', t('estimates.actions.requestApproval'), () => void onCommand({ action: 'request-approval' }), 'default')}
      {button('approve', t('estimates.actions.approve'), () => void onCommand({ action: 'approve' }), 'default')}
      {button('reject', t('estimates.actions.reject'), () => setRejectOpen(true), 'destructive')}
      {button('mark-presented', t('estimates.actions.markPresented'), () => void onCommand({ action: 'mark-presented' }))}
      {button('convert', t('estimates.actions.convert'), onConvert, 'default')}
      {button('revise', t('estimates.actions.revise'), () => void onCommand({ action: 'revise' }))}
      {button('mark-lost', t('estimates.actions.markLost'), () => setConfirm('mark-lost'))}
      {button('delete', t('estimates.actions.delete'), () => setConfirm('delete'), 'ghost')}
      {dirty && lifecycle.length > 0 && <span className="text-xs text-muted-foreground">{t('estimates.actions.saveFirst')}</span>}
      {blocked && <span className="text-xs text-destructive">{t('estimates.actions.blocked')}</span>}
      <RejectDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        pending={pending}
        onConfirm={(comment) => onCommand({ action: 'reject', comment })}
      />
      <AlertDialog open={confirm !== null} onOpenChange={(open) => { if (!open) setConfirm(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm === 'delete' ? t('estimates.confirm.deleteTitle') : t('estimates.confirm.lostTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{confirm === 'delete' ? t('estimates.confirm.deleteBody') : t('estimates.confirm.lostBody')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('estimates.actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirm === 'delete') onDelete();
                else void onCommand({ action: 'mark-lost' });
                setConfirm(null);
              }}
            >
              {confirm === 'delete' ? t('estimates.actions.delete') : t('estimates.actions.markLost')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
